import { useState, type ReactNode } from 'react';
import type { CatalogSwitch } from './catalogSwitch';
import type { CatalogEntry } from './catalogItems';
import type { CategoryDef } from './catalogCategories';
import { ConfirmDialog } from './ConfirmDialog';
import { boredSpots } from './modelsStages';
import { writtenPartName, writtenPartRows } from './openaiBlenderApi';
import { partCatalogId } from './partIdentity';
import { usePartModelJobs } from './partModels';
import type { PartItem } from './partsRun';
import { pieceOf, type Job3D, type PieceItem } from './threeDApi';
import { useLanguage } from '../../contexts/LanguageContext';

// THE WHOLE PRODUCT, CUT INTO ITS COMPONENTS — and the one thing a cut can cost.
//
// Keeping a written product in the Parts DB cuts it into one mesh per component part (see
// _keep_pieces), and those are the component parts' models: they come from nowhere else.
//
// SO IT ASKS BEFORE A CUT THAT WOULD LOSE HOLES. Switching a product off, or on again,
// deletes the pieces it was cut into, files and all (see _forget_pieces); keeping another
// product puts newer pieces in front of the old ones. A piece that has been drilled takes its
// holes with it either way, so the press first names what would be lost.

// THE COMPONENT PARTS A PRODUCT IS CUT INTO: the name each was modelled under and the catalog
// row it is filed under.
export function componentRows(
  items: PartItem[],
  categories: CategoryDef[],
  catalog: CatalogEntry[],
  loaded: boolean,
): (PieceItem & { letter: string | null })[] {
  return writtenPartRows(items, categories)
    .map((at) => ({
      name: writtenPartName(items[at]),
      // The manual's letter, for matching an imported object by it (see ProductImport).
      letter: items[at].letter,
      itemId: partCatalogId(catalog, loaded, items[at]),
    }))
    .filter((p): p is PieceItem & { letter: string | null } => p.itemId != null);
}

// Each component part and the model it holds in the Parts DB, if that model is a piece of a
// product — the only kind that counts.
function useCutModels(components: PieceItem[]): { part: PieceItem; job: Job3D | null }[] {
  const { modelOf } = usePartModelJobs();
  return components.map((part) => {
    const job = modelOf.get(part.itemId) ?? null;
    return { part, job: pieceOf(job) ? job : null };
  });
}

// THE PRODUCT'S DATABASE SWITCH, asking first where the press would lose drilled holes: the
// pieces this run was cut into, on either press, and — switching another run on — every
// drilled piece, which the new cut puts newer models in front of.
export function useCutGuard(
  catalog: CatalogSwitch,
  components: PieceItem[],
): { catalog: CatalogSwitch; dialog: ReactNode } {
  const { t } = useLanguage();
  const models = useCutModels(components);
  const [asking, setAsking] = useState<{ job: Job3D; on: boolean; lost: string[] } | null>(null);

  const onToggle = (job: Job3D, on: boolean) => {
    const lost = models
      .filter(({ job: piece }) => piece
        // ONLY A RUN SWITCHED ON, AND NOT THIS ONE: taking a product out keeps its pieces, and
        // putting the same one back keeps them again as they were (see _unkeep_pieces) — the
        // holes go out of view only when another run's fresh pieces take their place.
        && on && pieceOf(piece)?.of !== job.id
        && boredSpots(piece).length > 0)
      .map(({ part, job: piece }) => t('notice.models.cutLoses')
        .replace('{name}', part.name)
        .replace('{n}', String(boredSpots(piece).length)));
    if (!lost.length) return catalog.onToggle(job, on);
    setAsking({ job, on, lost });
  };

  const dialog = asking && (
    <ConfirmDialog
      title={t('notice.models.cutAskTitle')}
      body={`${t('notice.models.cutAskBody')}\n\n${asking.lost.join('\n')}`}
      confirmLabel={t('notice.models.cutAskGo')}
      cancelLabel={t('notice.models.cutAskCancel')}
      onConfirm={() => {
        catalog.onToggle(asking.job, asking.on);
        setAsking(null);
      }}
      onCancel={() => setAsking(null)}
    />
  );
  return { catalog: { ...catalog, onToggle }, dialog };
}
